'use client';
import { Button } from '../../ui/button/button';
import { ResponseNativeCard } from '@/src/interfaces/data-contracts';
import { CampaignTick } from '../../ui/icons/icons';
import Image from 'next/image';
import Modal from '../../ui/modal/modal';
import { useState } from 'react';
import LinkLikeButtonV2 from '../../shared/link-like-button-v2';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import fmt from '@/src/utils/helpers/number-format';

type Props = {
  item: ResponseNativeCard;
};

export const ProgrammableNativeCard: React.FC<Props> = ({ item }) => {
  const [showModal, setShowModal] = useState(false);
  const campaigns = item.nativeCardOfferCampaigns ?? [];
  const description = campaigns.map((c) => c.description).join(' ');

  const hasCampaigns = campaigns.length > 0;
  const firstCampaignType = hasCampaigns ? campaigns[0].type : undefined;
  const isCampaignTypeOne = hasCampaigns && (firstCampaignType as unknown as number) === 1;
  const isCampaignTypeNull = hasCampaigns && firstCampaignType === null;

  function handleCloseModal() {
    setShowModal(false);
  }
  return (
    <>
      <Modal
        show={showModal}
        onClose={handleCloseModal}
        customSize
        className="w-[calc(100%-32px)] rounded-xl px-4 py-5 md:w-[600px] md:px-10 md:py-10"
      >
        <div
          className="max-h-[calc(100vh-300px)] overflow-auto"
          dangerouslySetInnerHTML={{ __html: item.conditions ?? '' }}
        />
      </Modal>
      <div className="relative flex w-full flex-col items-center justify-between rounded-xl p-4 pt-12 md:min-h-[146px] md:max-w-[1120px] md:flex-row md:px-[14px] md:py-[24px]">
        {!isCampaignTypeNull && (
          <div className="absolute bottom-2 left-6 hidden text-xs md:block">{item.offerSpecialDescription}</div>
        )}

        <div className="flex w-full flex-col items-center justify-between gap-4 md:ml-[10px] md:w-[calc(100%-320px)] md:flex-row md:gap-0">
          <div className="flex w-full justify-between gap-4 md:w-min md:items-center">
            <div className="relative flex h-[48px] w-[104px] items-center justify-center rounded-md border border-gray-300 p-3 md:h-[66px] md:w-[122px]">
              {item.nativeCardPictures?.desktopImagePath && (
                <Image
                  src={item.nativeCardPictures?.desktopImagePath}
                  alt={item.nativeCardPictures?.altTitle ?? 'banka logo'}
                  className="hidden h-8 md:block"
                  fill
                />
              )}
              {item.nativeCardPictures?.tabletImagePath && (
                <Image
                  src={item.nativeCardPictures?.tabletImagePath}
                  alt={item.nativeCardPictures?.altTitle ?? 'banka logo'}
                  className="h-8 md:hidden"
                  fill
                />
              )}
              {item.nativeCardPictures?.mobileImagePath && (
                <Image
                  src={item.nativeCardPictures?.mobileImagePath}
                  alt={item.nativeCardPictures?.altTitle ?? 'banka logo'}
                  className="h-8 md:hidden"
                  fill
                />
              )}
            </div>

            <div className="block w-[132px] text-xs font-semibold text-gray-700 md:hidden">
              <div className="text-right font-semibold text-black800">
                {!isCampaignTypeNull && item.offerSpecialDescription}
                {!hasCampaigns && <div className="text-right font-semibold text-black800">{item.offerProductName}</div>}
              </div>
              {isCampaignTypeNull && hasCampaigns && (
                <div className="text-right font-semibold text-black800">{item.offerProductName}</div>
              )}
            </div>
          </div>

          <div className="flex w-full flex-col text-black800 md:ml-9 md:inline-flex md:flex-row md:items-center md:gap-[86px]">
            <div className="hidden w-[186px] font-semibold text-gray-700 md:block md:max-w-[200px]">
              {item.offerProductName}
            </div>
            <div className="w-full justify-between md:border-l md:border-black300">
              {!hasCampaigns ? (
                <div className="flex w-full items-center justify-between gap-[45px] md:ml-5 md:gap-5">
                  <div className="flex w-[90px] shrink-0 flex-col items-start gap-1 md:min-w-[99px] md:items-start">
                    <div className="overflow-hidden text-ellipsis whitespace-nowrap text-right text-xs text-[#626873] md:w-auto md:text-right">
                      {item.offerRateText}
                    </div>
                    <div className="text-base font-semibold">{item.rate ? item.rate : <br />}</div>
                  </div>

                  <div className="flex w-full items-center justify-between md:items-end md:justify-normal md:gap-[20px]">
                    <div className="flex flex-col items-end gap-1 md:min-w-[80px] md:items-end">
                      <div className="w-[50px] text-right text-xs text-[#626873] md:w-[80px] md:text-right">Vade</div>
                      <div className="w-[50px] text-right text-base font-semibold md:w-[80px] md:text-right">
                        {item.maturity} Ay
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 md:min-w-[124px] md:items-end">
                      <div className="text-xs text-[#626873]">{item.offerAmountText}</div>
                      {item.amount && (
                        <div className="text-base font-semibold">
                          {fmt(item.amount, CurrencyTypeMissing.TRY, true, false, 0)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : isCampaignTypeOne ? (
                campaigns.map((campaign) => (
                  <div key={campaign.description} className="flex items-center gap-1 md:ml-6 md:items-start">
                    <CampaignTick className="hidden shrink-0 md:block" width={18} height={18} />
                    {/*<div className="text-sm font-semibold text-[#2b3a42]">{campaign.description}</div>*/}
                    <div dangerouslySetInnerHTML={{ __html: campaign.description ?? '' }} />
                  </div>
                ))
              ) : isCampaignTypeNull ? (
                <div className="text-sm font-semibold md:ml-6 md:w-5/6">
                  <div dangerouslySetInnerHTML={{ __html: description ?? '' }} />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-4 flex w-full flex-row-reverse items-center justify-between gap-3 md:mt-0 md:w-min md:flex-col md:items-end">
          {item.conditions && item.isConditionActive ? (
            <Button
              onClick={() => setShowModal(true)}
              text={item.offerCTAText ?? ''}
              className="h-[48px] w-[182px] rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md transition"
            />
          ) : (
            item.link &&
            item.offerCTAText && (
              <LinkLikeButtonV2
                text={item.offerCTAText}
                href={item.link}
                target={item.target ?? '_blank'}
                className="h-[48px] w-[182px]"
              />
            )
          )}

          {(() => {
            if (hasCampaigns && isCampaignTypeNull && item.isConditionActive) {
              return (
                <button
                  onClick={() => setShowModal(true)}
                  className="hidden h-[48px] w-[106px] items-center justify-center px-4 text-center text-sm font-bold text-purple500 underline hover:underline md:flex md:w-[182px]"
                >
                  Koşullar
                </button>
              );
            }
            if (!hasCampaigns || !(hasCampaigns && isCampaignTypeOne)) {
              return (
                item.isConditionActive && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="flex h-[48px] w-[106px] items-center justify-center px-4 text-center text-sm font-bold text-purple500 underline hover:underline md:mt-4 md:w-[182px]"
                  >
                    Koşullar
                  </button>
                )
              );
            }
            return (
              <>
                {item.isConditionActive && (
                  <button
                    onClick={() => setShowModal(true)}
                    className="hidden h-[48px] w-[106px] items-center justify-center px-4 text-center text-sm font-bold text-purple500 underline hover:underline md:flex md:w-[182px]"
                  >
                    Koşullar
                  </button>
                )}
                <div className="md:hidden">
                  <div className="text-xs text-[#2b3a42bf]">Toplam</div>
                  {item.amount && (
                    <div className="text-lg font-semibold">
                      {fmt(item.amount, CurrencyTypeMissing.TRY, true, false, 0)}
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
};
